/**
 * Phase 3 — Validierung (Human-in-the-Loop).
 *
 * Production code path:
 *  - User öffnet TimesheetDetailPage
 *  - Cells mit Confidence + Plausibility (Rule A/B/C) → AttentionCounter
 *  - User korrigiert Werte (PATCH /api/timesheets/:id mit data-Patch)
 *  - hub mergeCardData appendet corrections[] server-side pro Zelle (Migration 034)
 *  - User klickt "Validieren" → POST /api/timesheets/:id/validate
 *  - Hub: state = validated, enqueue calculate-Job
 *
 * Glass-Box-Anker (Step 4) liest data.days[N].field.corrections[] und
 * data.days[N].field.ocr.* direkt aus der Karte — keine separate API-Roundtrip-
 * Quelle mehr (feedback_records-Tabelle entfiel mit Migration 034).
 */
import { Link, useLocation } from "react-router-dom";
import type { WerkstattCardContext } from "../../pages/WerkstattPage";
import type { Employee } from "../../lib/api";
import { PipelineStepList, type PipelineStep } from "../SettingsTestOcr";
import { PhaseHeader, withUuids } from "./Phase1Upload";
import { stepUuid } from "../../lib/stepUuid";
import { cellPlausibility } from "../../lib/plausibility";

// Auto-Match-Schwellen (worker/src/config.ts: OCR_NAME_MATCH_THRESHOLD / _SUGGEST_MIN).
const NAME_MATCH_THRESHOLD = 0.80;
const NAME_MATCH_SUGGEST_MIN = 0.60;

/**
 * Debug-Raum (Child von Validierung-Schritt 3): zeigt, WARUM der Employee-Auto-Match
 * nicht griff — der gelesene Header-Text, dessen Confidence, die Match-Kandidaten mit
 * Scores, die Schwellen und eine Klartext-Diagnose. Quelle: card.data.header
 * (employee_raw/dienstnehmer_raw, employee_confidence, suggestions[]) + Mandanten-Stamm.
 */
function EmployeeMatchDebug({ header, employees, employeeId }: {
  header: any; employees: Employee[]; employeeId: string | null;
}) {
  const raw = String(header?.employee_raw ?? header?.dienstnehmer_raw ?? "").trim();
  const conf: number | null = typeof header?.employee_confidence === "number" ? header.employee_confidence
    : typeof header?.employee_match_score === "number" ? header.employee_match_score : null;
  const suggestions: Array<{ id: string; name: string; score: number }> =
    Array.isArray(header?.suggestions) ? header.suggestions : [];
  const best = suggestions.length ? Math.max(...suggestions.map((s) => s.score ?? 0)) : null;

  let diagnosis: { tone: "ok" | "warn" | "err"; text: string };
  if (employeeId) diagnosis = { tone: "ok", text: "Mitarbeiter ist zugeordnet." };
  else if (!raw) diagnosis = { tone: "err", text:
    "❗ Header-OCR hat KEINEN Namen gelesen (employee_raw leer, Confidence 0). Es gibt nichts zum Matchen — "
    + "die Ursache liegt in der HEADER-EXTRAKTION (Header-Region des PDFs / OCR), NICHT im Namens-Matching." };
  else if (suggestions.length === 0) diagnosis = { tone: "warn", text:
    `Name gelesen („${raw}"), aber kein Kandidat ≥ ${NAME_MATCH_SUGGEST_MIN} (suggest_min). Der Name ist nicht im `
    + "Mandanten-Stamm oder zu abweichend geschrieben." };
  else if (best != null && best >= NAME_MATCH_THRESHOLD) diagnosis = { tone: "warn", text:
    `Mehrere Kandidaten ≥ ${NAME_MATCH_THRESHOLD} → mehrdeutig, manuelle Wahl nötig.` };
  else diagnosis = { tone: "warn", text:
    `Bester Kandidat-Score ${best?.toFixed(2) ?? "—"} < ${NAME_MATCH_THRESHOLD} (Auto-Schwelle) → kein eindeutiger `
    + "Auto-Match, manuelle Wahl nötig." };

  const toneColor = diagnosis.tone === "err" ? "#991b1b" : diagnosis.tone === "warn" ? "#92400e" : "#065f46";
  const toneBg = diagnosis.tone === "err" ? "#fef2f2" : diagnosis.tone === "warn" ? "#fffbeb" : "#ecfdf5";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12 }}>
      <div style={{ padding: "6px 10px", borderRadius: 4, background: toneBg, color: toneColor, fontWeight: 600 }}>
        {diagnosis.text}
      </div>
      <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
        <tbody>
          <tr><td style={{ padding: "2px 10px 2px 0", color: "#6b7280" }}>Gelesener Name (Header-OCR)</td>
            <td><code>{raw || "(nichts erkannt)"}</code></td></tr>
          <tr><td style={{ padding: "2px 10px 2px 0", color: "#6b7280" }}>Confidence</td>
            <td><code>{conf != null ? conf.toFixed(2) : "—"}</code></td></tr>
          <tr><td style={{ padding: "2px 10px 2px 0", color: "#6b7280" }}>Schwellen</td>
            <td>Auto ≥ <code>{NAME_MATCH_THRESHOLD}</code> · Vorschlag ≥ <code>{NAME_MATCH_SUGGEST_MIN}</code></td></tr>
          <tr><td style={{ padding: "2px 10px 2px 0", color: "#6b7280" }}>Mitarbeiter im Stamm</td>
            <td><code>{employees.length}</code></td></tr>
        </tbody>
      </table>
      <div>
        <div style={{ fontWeight: 600, color: "#374151", marginBottom: 2 }}>Match-Kandidaten ({suggestions.length})</div>
        {suggestions.length === 0 ? (
          <div style={{ color: "#9ca3af" }}>— keine (kein Kandidat ≥ {NAME_MATCH_SUGGEST_MIN})</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ textAlign: "left", color: "#6b7280" }}>
              <th style={{ padding: "2px 8px" }}>Name</th><th style={{ padding: "2px 8px" }}>Score</th><th style={{ padding: "2px 8px" }}>≥ Auto?</th>
            </tr></thead>
            <tbody>
              {suggestions.slice().sort((a, b) => (b.score ?? 0) - (a.score ?? 0)).map((s) => (
                <tr key={s.id} style={{ borderTop: "1px solid #eee" }}>
                  <td style={{ padding: "2px 8px" }}>{s.name}</td>
                  <td style={{ padding: "2px 8px" }}><code>{(s.score ?? 0).toFixed(2)}</code></td>
                  <td style={{ padding: "2px 8px" }}>{(s.score ?? 0) >= NAME_MATCH_THRESHOLD ? "✓" : (s.score ?? 0) >= NAME_MATCH_SUGGEST_MIN ? "Vorschlag" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const FIELDS = ["von1", "bis1", "von2", "bis2", "symb"] as const;

interface CellSummary {
  day: number;
  field: string;
  ocr_value: string | null;
  ocr_model: string | null;
  ocr_conf: number | null;
  current_value: string | null;
  corrections_count: number;
  last_corrected_at: string | null;
  has_ocr_layer: boolean;
  is_corrected: boolean;                // current_value !== ocr_value
  is_untracked_drift: boolean;          // is_corrected && corrections_count === 0
}

export function Phase3Validation({ ctx }: { ctx: WerkstattCardContext }) {
  const c = ctx.card;
  const isExpense = c?.kind === "expense" || c?.kind === "unknown";
  if (isExpense) return <ExpenseValidationBody ctx={ctx} />;
  return <TimesheetValidationBody ctx={ctx} />;
}

function TimesheetValidationBody({ ctx }: { ctx: WerkstattCardContext }) {
  const c = ctx.card;
  const t = ctx.timesheet;

  const metrics = computeValidationMetrics(t?.data);
  const provenance = computeProvenance(t?.data);

  const steps: PipelineStep[] = [
    {
      num: 1,
      title: "Cells mit niedriger Confidence markiert",
      description:
        "Per-Cell-Confidence aus OCR (Phase 2) wird mit thresholds (cellStatus) verglichen. "
        + "Cells unter Schwellwert erscheinen in AttentionCounter, müssen vom User bestätigt oder korrigiert werden. "
        + "Source: frontend/src/lib/cellStatus.ts + components/AttentionCounter.tsx",
      status: t ? "done" : "pending",
      detail: t
        ? `${metrics.cellsTotal} Cells gelesen · ${metrics.cellsLowConf} unter Schwellwert · ${metrics.cellsRed} rot, ${metrics.cellsYellow} gelb`
        : "(wartet auf OCR)",
    },
    {
      num: 2,
      title: "Plausibilität (Rule A/B/C)",
      description:
        "Rule A: Wert-Plausibilität (Minute-Rundung). "
        + "Rule B: Sequenz (von1 < bis1 < von2 < bis2). "
        + "Rule C: Symbol-Exklusivität (kein Symbol + Zeitwerte gleichzeitig). "
        + "Source: frontend/src/lib/plausibility.ts",
      status: t ? "done" : "pending",
      detail: t
        ? `${metrics.plausViolations} Verletzungen entdeckt (${metrics.plausDaysAffected} Tage betroffen)`
        : "(wartet)",
    },
    {
      num: 3,
      title: "Header-OCR + Employee-Auto-Match",
      description:
        "Aus header.suggestions wählt der Worker per Levenshtein den besten Match aus employees. "
        + "Bei mehreren Treffern oder Konflikten muss der User in der UI bestätigen.",
      status: c?.employee_id ? "done" : ctx.timesheet ? "waiting" : "pending",
      blockedReason: c && !c.employee_id ? "Mitarbeiter zuordnen (kein eindeutiger Auto-Match)" : undefined,
      detail: c?.employee_id
        ? `Auto-resolved: ${ctx.employee?.last_name ?? ""}, ${ctx.employee?.first_name ?? ""} (${c.employee_id.slice(0, 8)})`
        : t
          ? `header.suggestions: ${JSON.stringify(((t.data as any)?.header?.suggestions ?? []).slice(0, 3))}`
          : "(wartet)",
    },
    {
      num: 4,
      title: "Per-Cell-Provenance (Glass-Box-Anker)",
      description:
        "Pro Zelle: welcher OCR-Modellname mit welcher Prompt-Version hat sie "
        + "gelesen, was war der raw-output, welche Korrekturen hat der User danach "
        + "draufgelegt. Quelle: data.days[N].field.{ocr,value,corrections} direkt "
        + "aus der Karte — kein API-Roundtrip, keine feedback_records-Tabelle (weg "
        + "seit Migration 034). Drift-Check: wenn aktueller value != ocr.value aber "
        + "corrections.length === 0, dann ist die Karte über einen Pfad editiert "
        + "worden der den server-mergeCardData umgeht. Selbsttest des neuen Patterns.",
      status: !t
        ? "pending"
        : provenance.untracked.length > 0
          ? "error"
          : provenance.has_ocr_layer === false
            ? "pending"
            : "done",
      detail: !t
        ? "(wartet auf OCR)"
        : provenance.has_ocr_layer === false
          ? "Karte ohne ocr-Schicht — Re-OCR setzt sie (Migration 034 Big-Bang läuft)"
          : <ProvenanceDetail summary={provenance} />,
    },
    {
      num: 5,
      title: "User-Validierung",
      description:
        "User klickt 'Validieren' in der TimesheetDetailPage. Hub setzt state=validated und enqueued den calculate-Job. "
        + "Detail listet pro Cell die echte Korrektur (corrections[]): OCR-Wert → User-Wert + Zeitstempel. "
        + "Quelle: data.days[N].field.corrections[], server-side appended von hub/src/repos/timesheet.ts:mergeCardData.",
      status: c
        ? ["validated", "calculated", "exported"].includes(c.state) ? "done"
          : c.state === "ocr_complete" ? "pending"
          : "error"
        : "pending",
      detail: c ? <UserCorrectionsDetail card={c} /> : "(wartet)",
    },
    {
      num: 6,
      title: "Validierung = pauschaler User-OK auf alle Attention-Items",
      description:
        "Sobald state ∈ {validated, calculated, exported, archived, superseded}, "
        + "interpretiert die TimesheetDetailPage den Validate-Klick als implizite "
        + "Akzeptanz ALLER noch offenen confidence-low + plausibility-red/yellow Cells. "
        + "Der AttentionCounter geht auf 0, der Breakdown bleibt zur Audit-Sicht erhalten.",
      status: c
        ? ["validated", "calculated", "exported", "archived", "superseded"].includes(c.state) ? "done"
          : c.state === "ocr_complete" ? "pending"
          : "pending"
        : "pending",
      detail: c
        ? ["validated", "calculated", "exported", "archived", "superseded"].includes(c.state)
          ? "✓ Auto-Accept aktiv — AttentionCounter zeigt 0 trotz vorheriger Hinweise"
          : "(passiert sobald User „Validieren\" klickt — Step 5)"
        : "(wartet)",
      optional: true,
    },
  ];

  return (
    <div>
      <PhaseHeader
        num={3}
        title="Validierung (Human-in-the-Loop)"
        purpose="OCR-Output von Birgit prüfen, korrigieren, freigeben"
        inputs="card.data.days + data.header + card.state=ocr_complete"
        outputs="korrigierte data.days + card.employee_id + state=validated"
        prodFiles={[
          "frontend/src/pages/TimesheetDetailPage.tsx",
          "frontend/src/components/AttentionCounter.tsx",
          "frontend/src/lib/cellStatus.ts",
          "frontend/src/lib/plausibility.ts",
          "hub/src/routes/timesheets.ts",
          "hub/src/repos/timesheet.ts (mergeCardData)",
        ]}
        userGates="(1) Cells bestätigen/korrigieren  (2) Mitarbeiter aus Suggestions wählen  (3) Validieren klicken"
      />
      <PipelineStepList steps={withUuids(c?.id ?? null, "validierung", steps)} />
      {t && metrics.violationsList.length > 0 && (
        <div className="violations-list" style={{ marginTop: 16 }}>
          <strong>Plausibilitäts-Verletzungen ({metrics.violationsList.length}):</strong>
          <ul style={{ fontSize: 13, marginTop: 8 }}>
            {metrics.violationsList.slice(0, 20).map((v, i) => (
              <li key={i}><strong>Tag {v.day}</strong> {v.field}: {v.reason}</li>
            ))}
            {metrics.violationsList.length > 20 && (
              <li className="muted">… und {metrics.violationsList.length - 20} weitere</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Compact Detail-Render für den Per-Cell-Provenance-Step. Zeigt:
 *  - Summary-Zeile: X von Y Zellen korrigiert · 1-N Modelle beteiligt
 *  - Tabelle nur für Cells mit Corrections ODER Untracked-Drift
 *  - Rote Markierung wenn Untracked-Drift existiert
 */
function ProvenanceDetail({ summary }: { summary: ProvenanceSummary }) {
  const interesting = summary.cells.filter((c) => c.is_corrected || c.is_untracked_drift);
  const models = Array.from(new Set(summary.cells.map((c) => c.ocr_model).filter(Boolean))) as string[];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div>
        <strong>{summary.corrections_total}</strong> Korrektur(en) über{" "}
        <strong>{interesting.filter((c) => c.is_corrected).length}</strong> von{" "}
        <strong>{summary.cells_with_ocr}</strong> OCR-Zellen
        {models.length > 0 && (
          <> · Modelle: <code style={{ fontSize: 12 }}>{models.join(", ")}</code></>
        )}
      </div>
      {summary.untracked.length > 0 && (
        <div style={{ color: "#dc2626", fontWeight: 600 }}>
          ⚠ {summary.untracked.length} Korrektur(en) ohne corrections[]-Eintrag
          — Datenverlust (PATCH bypasste mergeCardData)
        </div>
      )}
      {interesting.length === 0 ? (
        <div className="muted" style={{ fontSize: 12, fontStyle: "italic" }}>
          Keine Korrekturen — alle OCR-Werte unverändert übernommen.
        </div>
      ) : (
        <table className="data-table" style={{ fontSize: 12, marginTop: 4 }}>
          <thead>
            <tr>
              <th>Tag</th>
              <th>Feld</th>
              <th>OCR-Wert</th>
              <th>Modell</th>
              <th>OCR-Conf</th>
              <th>Aktueller Wert</th>
              <th>Korrekturen</th>
            </tr>
          </thead>
          <tbody>
            {interesting.slice(0, 30).map((c, i) => (
              <tr key={i} style={c.is_untracked_drift ? { background: "#fef2f2" } : undefined}>
                <td>{c.day}</td>
                <td><code>{c.field}</code></td>
                <td>{c.ocr_value ?? "—"}</td>
                <td><code>{c.ocr_model ?? "—"}</code></td>
                <td>{c.ocr_conf != null ? (c.ocr_conf * 100).toFixed(0) + "%" : "—"}</td>
                <td><strong>{c.current_value ?? "—"}</strong></td>
                <td>
                  {c.is_untracked_drift ? (
                    <span style={{ color: "#dc2626", fontWeight: 600 }}>⚠ kein Eintrag</span>
                  ) : c.corrections_count > 0 ? (
                    <>
                      {c.corrections_count}↻
                      {c.last_corrected_at && (
                        <span className="muted" style={{ marginLeft: 6 }}>
                          {new Date(c.last_corrected_at).toLocaleString("de-AT")}
                        </span>
                      )}
                    </>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {interesting.length > 30 && (
        <div className="muted" style={{ fontSize: 11 }}>
          … {interesting.length - 30} weitere Zellen mit Korrekturen
        </div>
      )}
    </div>
  );
}

interface ProvenanceSummary {
  has_ocr_layer:     boolean;
  cells_with_ocr:    number;
  cells:             CellSummary[];
  corrections_total: number;
  untracked:         CellSummary[];
}

function computeProvenance(data: unknown): ProvenanceSummary {
  const out: ProvenanceSummary = {
    has_ocr_layer: false,
    cells_with_ocr: 0,
    cells: [],
    corrections_total: 0,
    untracked: [],
  };
  if (!data || typeof data !== "object") return out;
  const days = (data as any).days;
  if (!days || typeof days !== "object") return out;
  for (const [dayKey, dayRow] of Object.entries(days)) {
    if (!/^\d+$/.test(dayKey)) continue;
    if (!dayRow || typeof dayRow !== "object") continue;
    const day = Number(dayKey);
    for (const field of FIELDS) {
      const cell = (dayRow as any)[field];
      if (!cell || typeof cell !== "object") continue;
      const ocr = cell.ocr && typeof cell.ocr === "object" ? cell.ocr : null;
      const has_ocr = !!ocr;
      if (has_ocr) out.has_ocr_layer = true;
      if (has_ocr) out.cells_with_ocr++;
      const corrections = Array.isArray(cell.corrections) ? cell.corrections : [];
      const last = corrections[corrections.length - 1];
      const ocr_value = ocr ? (ocr.value ?? null) : null;
      const current_value = cell.value ?? null;
      const is_corrected = has_ocr && ocr_value !== current_value;
      const is_untracked_drift = is_corrected && corrections.length === 0;
      const summary: CellSummary = {
        day,
        field,
        ocr_value,
        ocr_model: ocr ? (ocr.model ?? null) : null,
        ocr_conf: ocr ? (typeof ocr.confidence === "number" ? ocr.confidence : null) : null,
        current_value,
        corrections_count: corrections.length,
        last_corrected_at: last && typeof last === "object" ? (last.corrected_at ?? null) : null,
        has_ocr_layer: has_ocr,
        is_corrected,
        is_untracked_drift,
      };
      out.cells.push(summary);
      out.corrections_total += corrections.length;
      if (is_untracked_drift) out.untracked.push(summary);
    }
  }
  return out;
}

interface ValidationMetrics {
  cellsTotal: number;
  cellsLowConf: number;
  cellsRed: number;
  cellsYellow: number;
  plausViolations: number;
  plausDaysAffected: number;
  violationsList: Array<{ day: number; field: string; reason: string }>;
}

function computeValidationMetrics(data: unknown): ValidationMetrics {
  const m: ValidationMetrics = {
    cellsTotal: 0, cellsLowConf: 0, cellsRed: 0, cellsYellow: 0,
    plausViolations: 0, plausDaysAffected: 0,
    violationsList: [],
  };
  if (!data || typeof data !== "object") return m;
  const days = (data as any).days ?? {};
  for (const [dayKey, dayData] of Object.entries(days)) {
    if (!/^\d+$/.test(dayKey)) continue;
    const day = Number(dayKey);
    const d = dayData as any;
    if (!d) continue;
    let dayHasViolation = false;
    for (const field of ["von1", "bis1", "von2", "bis2", "symb"]) {
      const cell = d[field];
      if (cell && typeof cell === "object" && "value" in cell && cell.value) {
        m.cellsTotal++;
        // OCR-Confidence aus ocr.confidence (neues Schema), Fallback auf cell.confidence
        const conf = cell.ocr?.confidence ?? cell.confidence;
        if (typeof conf === "number") {
          if (conf < 0.7) m.cellsRed++;
          else if (conf < 0.85) m.cellsYellow++;
          if (conf < 0.85) m.cellsLowConf++;
        }
      }
      // Plausibility on time cells only
      if (["von1", "bis1", "von2", "bis2"].includes(field)) {
        const dayObj: any = {};
        for (const f2 of ["von1", "bis1", "von2", "bis2"]) {
          const c2 = d[f2];
          dayObj[f2] = c2 && typeof c2 === "object" && "value" in c2 ? c2.value : (c2 ?? "");
        }
        const p = cellPlausibility(field as any, dayObj[field], dayObj);
        if (p.severity !== "ok") {
          m.plausViolations++;
          dayHasViolation = true;
          for (const r of p.reasons) {
            m.violationsList.push({ day, field, reason: r });
          }
        }
      }
    }
    if (dayHasViolation) m.plausDaysAffected++;
  }
  return m;
}

/**
 * Liest data.days[N].field.corrections[] aus und liefert eine flache,
 * zeitlich sortierte Liste aller User-Korrekturen mit ihrem OCR-Vorzustand.
 * Quelle ist das corrections-Array das server-side von mergeCardData
 * (hub/src/repos/timesheet.ts) bei jedem PATCH appended wird.
 */
interface UserCorrection {
  day: number;
  field: string;
  from: string | null;
  to: string | null;
  corrected_at: string | null;
  user_id: string | null;
  ocr_run_id: string | null;
}

function computeUserCorrections(data: unknown): UserCorrection[] {
  const out: UserCorrection[] = [];
  if (!data || typeof data !== "object") return out;
  const days = (data as any).days;
  if (!days || typeof days !== "object") return out;
  for (const [dayKey, dayRow] of Object.entries(days)) {
    if (!/^\d+$/.test(dayKey)) continue;
    const day = Number(dayKey);
    if (!dayRow || typeof dayRow !== "object") continue;
    for (const field of FIELDS) {
      const cell = (dayRow as any)[field];
      if (!cell || typeof cell !== "object") continue;
      const corrections = Array.isArray(cell.corrections) ? cell.corrections : [];
      for (const corr of corrections) {
        if (!corr || typeof corr !== "object") continue;
        out.push({
          day, field,
          from: corr.from ?? null,
          to: corr.to ?? null,
          corrected_at: corr.corrected_at ?? null,
          user_id: corr.user_id ?? null,
          ocr_run_id: corr.ocr_run_id ?? null,
        });
      }
    }
  }
  // Zeitlich sortiert (älteste zuerst). Fallback auf Tag+Feld wenn at fehlt.
  out.sort((a, b) => {
    if (a.corrected_at && b.corrected_at) return a.corrected_at.localeCompare(b.corrected_at);
    if (a.day !== b.day) return a.day - b.day;
    return a.field.localeCompare(b.field);
  });
  return out;
}

/** Detail für Step 5 (User-Validierung): konkrete User-Korrekturen mit
 *  OCR-Vorzustand und Zeitstempel. Wird IMMER gerendert sobald die Karte
 *  existiert — Korrekturen passieren VOR dem Validierungs-Klick (User
 *  editiert in der Detail-UI mit Speichern, state bleibt ocr_complete).
 *  Header signalisiert ob der Validierungs-Klick schon passiert ist. */
function UserCorrectionsDetail({ card }: { card: any }) {
  const corrections = computeUserCorrections(card?.data);
  const validated = ["validated", "calculated", "exported", "archived", "superseded"].includes(card.state);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div>
        {validated ? (
          <>✓ Validiert · state=<code>{card.state}</code></>
        ) : card.state === "ocr_complete" ? (
          <>⏸ Wartet auf User-Klick „Validieren"</>
        ) : (
          <>state=<code>{card.state}</code> (außerhalb Validierungs-Flow)</>
        )}
        {" · "}<strong>{corrections.length}</strong> User-Korrektur(en){" "}
        <Link to={`/timesheets/${card.id}`} style={{ color: "#6b7280", marginLeft: 8 }}>
          Validierungs-UI öffnen{validated ? " (Review)" : ""} →
        </Link>
      </div>
      {corrections.length === 0 ? (
        <div className="muted" style={{ fontSize: 12, fontStyle: "italic" }}>
          Keine User-Korrekturen — OCR-Werte 1:1 übernommen.
        </div>
      ) : (
        <table className="data-table" style={{ fontSize: 12, marginTop: 4 }}>
          <thead>
            <tr>
              <th>Tag</th>
              <th>Feld</th>
              <th>OCR-Wert (vorher)</th>
              <th>User-Wert (nachher)</th>
              <th>Wann</th>
            </tr>
          </thead>
          <tbody>
            {corrections.slice(0, 50).map((c, i) => (
              <tr key={i}>
                <td>{c.day}</td>
                <td><code>{c.field}</code></td>
                <td>{c.from === null || c.from === "" ? <span className="muted">(leer)</span> : c.from}</td>
                <td><strong>{c.to === null || c.to === "" ? <span className="muted">(leer)</span> : c.to}</strong></td>
                <td className="muted">
                  {c.corrected_at ? new Date(c.corrected_at).toLocaleString("de-AT") : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {corrections.length > 50 && (
        <div className="muted" style={{ fontSize: 11 }}>
          … {corrections.length - 50} weitere Korrekturen
        </div>
      )}
    </div>
  );
}

// ─── Diäten-Pfad ───────────────────────────────────────────────────────────
//
// Phase 3 (Validierung) für Diäten-Karten. Die Logik spiegelt den Stundenliste-
// Pfad, die Rules unterscheiden sich:
//   · Plausibility-Regel A: abfahrt < rueckkehr pro Eintrag
//   · Plausibility-Regel B: datum monoton aufsteigend
//   · Plausibility-Regel C: alle datums liegen im Monat der Karte
//   · Plausibility-Regel D: kd_nr numerisch (1-4 Ziffern)
// Per-Cell-Provenance-Schicht (Step 4) wartet auf die Diäten-Schema-Migration:
// data.entries[i].field.{ocr,corrections} ist analog zu days[N].field — heute
// noch slim cells {value, confidence}.

interface ExpensePlausViolation {
  entry_idx: number;
  field: string;
  reason: string;
}

interface ExpenseValidationMetrics {
  cellsTotal: number;
  cellsLowConf: number;
  entriesTotal: number;
  plausViolations: number;
  entriesAffected: number;
  violationsList: ExpensePlausViolation[];
}

function computeExpenseValidationMetrics(data: unknown, cardMonth: string | null | undefined): ExpenseValidationMetrics {
  const m: ExpenseValidationMetrics = {
    cellsTotal: 0, cellsLowConf: 0,
    entriesTotal: 0, plausViolations: 0, entriesAffected: 0,
    violationsList: [],
  };
  if (!data || typeof data !== "object") return m;
  const entries: any[] = Array.isArray((data as any).entries) ? (data as any).entries : [];
  m.entriesTotal = entries.length;

  const FIELDS_EX = ["tag", "datum", "kd_nr", "kunde", "zm_abzug", "m_verr", "ort", "abfahrt", "rueckkehr"];
  let prevDatum: Date | null = null;
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i];
    if (!e || typeof e !== "object") continue;
    let entryHasViolation = false;
    const cellValue = (key: string): string => {
      const cell = e[key];
      if (!cell) return "";
      if (typeof cell === "string") return cell;
      if (typeof cell === "object" && "value" in cell) return (cell.value ?? "") as string;
      return "";
    };

    // Confidence-Zählung
    for (const f of FIELDS_EX) {
      const cell = e[f];
      if (cell && typeof cell === "object" && cell.value) {
        m.cellsTotal++;
        const conf = (cell as any).ocr?.confidence ?? cell.confidence;
        if (typeof conf === "number" && conf < 0.85) m.cellsLowConf++;
      }
    }

    const abfahrt = cellValue("abfahrt");
    const rueckkehr = cellValue("rueckkehr");
    if (abfahrt && rueckkehr) {
      const a = parseTimeMinutes(abfahrt);
      const r = parseTimeMinutes(rueckkehr);
      if (a !== null && r !== null && a >= r) {
        m.violationsList.push({ entry_idx: i, field: "abfahrt", reason: `abfahrt ${abfahrt} ≥ rueckkehr ${rueckkehr}` });
        m.plausViolations++;
        entryHasViolation = true;
      }
    }

    const datumStr = cellValue("datum");
    if (datumStr) {
      const parsed = parseDatum(datumStr);
      if (parsed) {
        if (prevDatum && parsed < prevDatum) {
          m.violationsList.push({ entry_idx: i, field: "datum", reason: `datum ${datumStr} liegt vor vorherigem Eintrag` });
          m.plausViolations++;
          entryHasViolation = true;
        }
        prevDatum = parsed;
        if (cardMonth && parsed) {
          const ymd = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`;
          if (ymd !== cardMonth) {
            m.violationsList.push({ entry_idx: i, field: "datum", reason: `datum ${datumStr} → ${ymd}, erwartet ${cardMonth}` });
            m.plausViolations++;
            entryHasViolation = true;
          }
        }
      } else {
        m.violationsList.push({ entry_idx: i, field: "datum", reason: `datum '${datumStr}' nicht parsbar (TT.MM oder TT.MM.JJJJ)` });
        m.plausViolations++;
        entryHasViolation = true;
      }
    }

    const kdNr = cellValue("kd_nr");
    if (kdNr && !/^\d{1,4}$/.test(kdNr.trim())) {
      m.violationsList.push({ entry_idx: i, field: "kd_nr", reason: `kd_nr '${kdNr}' nicht numerisch (1-4 Ziffern)` });
      m.plausViolations++;
      entryHasViolation = true;
    }

    if (entryHasViolation) m.entriesAffected++;
  }
  return m;
}

function parseTimeMinutes(s: string): number | null {
  const m = s.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1]!, 10); const mm = parseInt(m[2]!, 10);
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
}

function parseDatum(s: string): Date | null {
  // Akzeptiert TT.MM, TT.MM.JJ, TT.MM.JJJJ
  const m = s.trim().match(/^(\d{1,2})\.(\d{1,2})(?:\.(\d{2,4}))?$/);
  if (!m) return null;
  const d = parseInt(m[1]!, 10);
  const mo = parseInt(m[2]!, 10);
  let y = m[3] ? parseInt(m[3], 10) : new Date().getFullYear();
  if (y < 100) y += 2000;
  if (d < 1 || d > 31 || mo < 1 || mo > 12) return null;
  return new Date(y, mo - 1, d);
}

function ExpenseValidationBody({ ctx }: { ctx: WerkstattCardContext }) {
  const c = ctx.card!;
  const loc = useLocation();
  const data = (c as any).data ?? {};
  const entries: any[] = Array.isArray(data.entries) ? data.entries : [];
  const header = data.header ?? null;
  const month = (c as any).month ?? null;
  const metrics = computeExpenseValidationMetrics(data, month);
  const hasOcr = entries.length > 0 || ["ocr_complete", "validated", "calculated", "exported"].includes(c.state);

  const steps: PipelineStep[] = [
    {
      num: 1,
      title: "Cells mit niedriger Confidence markiert",
      description:
        "Confidence-Filter analog zur Stundenliste, aber über data.entries[i].field "
        + "statt data.days[N].field. AttentionCounter zeigt cells mit conf < 0.85.",
      status: hasOcr ? "done" : "pending",
      detail: hasOcr
        ? `${metrics.cellsTotal} Cells gelesen über ${metrics.entriesTotal} Einträge · ${metrics.cellsLowConf} unter Schwellwert 0.85`
        : "(wartet auf OCR)",
    },
    {
      num: 2,
      title: "Plausibility-Rules (Diäten-spezifisch)",
      description:
        "Vier Rules statt der drei der Stundenliste:\n"
        + "  · Rule A: abfahrt < rueckkehr pro Eintrag\n"
        + "  · Rule B: datum-Spalte monoton aufsteigend\n"
        + "  · Rule C: datum liegt im Monat der Karte (z.B. April → alle datums 2026-04-xx)\n"
        + "  · Rule D: kd_nr ist numerisch (1-4 Ziffern)\n"
        + "Implementierung folgt — heute compute-time im Frontend (Vorschau für die Production-Lib).",
      status: hasOcr ? "done" : "pending",
      detail: hasOcr
        ? `${metrics.plausViolations} Verletzungen in ${metrics.entriesAffected} Einträgen`
        : "(wartet)",
    },
    {
      num: 3,
      title: "Header-OCR + Employee-Auto-Match — geshared",
      description:
        "Identisch zur Stundenliste — Hub-Code worker/src/handlers/ocr.ts ist "
        + "kind-agnostic, Levenshtein-Match gegen employees.",
      status: hasOcr ? (c.employee_id ? "done" : "waiting") : "pending",
      blockedReason: hasOcr && !c.employee_id ? "Mitarbeiter zuordnen (kein eindeutiger Auto-Match)" : undefined,
      detail: !hasOcr ? "(wartet)"
        : c.employee_id
          ? `Auto-resolved: ${ctx.employee?.last_name ?? ""}, ${ctx.employee?.first_name ?? ""} (${c.employee_id.slice(0, 8)})`
          : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <span>Kein eindeutiger Auto-Match — gelesener Name: <code>{header?.employee_raw || "(nichts erkannt)"}</code></span>
              <Link
                to={`/expenses/${c.id}`}
                state={{ background: loc }}
                style={{
                  alignSelf: "flex-start", display: "inline-block", padding: "6px 14px",
                  background: "#b45309", color: "white", borderRadius: 4, textDecoration: "none",
                  fontSize: 13, fontWeight: 600,
                }}
                title="Öffnet die Validierungs-UI mit Mitarbeiter-Auswahl (Dropdown + OCR-Vorschläge)"
              >✋ Mitarbeiter zuordnen →</Link>
            </div>
          ),
      // Drill-in: ein Raum tiefer — Debug-Evidenz, WARUM kein Auto-Match.
      children: hasOcr && !c.employee_id ? [{
        num: 1,
        title: "🔍 Debug: Warum kein Employee-Match?",
        description: "Gelesener Header-Name + Confidence + Match-Kandidaten + Schwellen + Klartext-Diagnose.",
        status: "waiting" as const,
        uuid: stepUuid(c.id, "validierung", "employee-match-debug"),
        detail: <EmployeeMatchDebug header={header} employees={ctx.employees} employeeId={c.employee_id} />,
      }] : undefined,
    },
    {
      num: 4,
      title: "Per-Cell-Provenance (geplant)",
      description:
        "Analog zur Stundenliste (Migration 034), aber für entries[i].field. "
        + "Aktuell schreibt der Legacy-paddle_vl-Path slim cells {value,confidence}, "
        + "ohne ocr-Schicht + corrections-Stack. Diäten-Schema-Migration ist "
        + "nächste Phase: data.entries[i].field.{ocr,value,corrections}.",
      status: "pending",
      detail: hasOcr
        ? `${metrics.cellsTotal} Cells aktuell ohne ocr-Layer (Legacy slim cells)`
        : "(wartet)",
      optional: true,
    },
    {
      num: 5,
      title: "User-Validierung",
      description:
        "User klickt 'Validieren' in der ExpenseDetailPage. Hub setzt state=validated.",
      status: c
        ? ["validated", "calculated", "exported"].includes(c.state) ? "done"
          : c.state === "ocr_complete" ? "pending"
          : c.state === "pending_review" ? "error"
          : "pending"
        : "pending",
      detail: c ? (
        c.state === "ocr_complete" || c.state === "pending_review" ? (
          <>
            ⏸ Wartet auf User-Klick „Validieren" · {" "}
            <Link to={`/expenses/${c.id}`} style={{ fontWeight: 600 }}>
              Validierungs-UI öffnen →
            </Link>
          </>
        ) : ["validated", "calculated", "exported"].includes(c.state) ? (
          <>✓ Validiert · state={c.state}</>
        ) : (
          <>state={c.state}</>
        )
      ) : "(wartet)",
    },
    {
      num: 6,
      title: "Validierung = pauschaler User-OK auf alle Attention-Items — geshared",
      description:
        "Gleicher Auto-Accept-Mechanismus wie Stundenliste. Nach Validate-Klick "
        + "verschwinden alle confidence-low + plausibility-Hinweise aus AttentionCounter.",
      status: c
        ? ["validated", "calculated", "exported", "archived", "superseded"].includes(c.state) ? "done"
          : "pending"
        : "pending",
      detail: c
        ? ["validated", "calculated", "exported"].includes(c.state)
          ? "✓ Auto-Accept aktiv"
          : "(passiert sobald User „Validieren\" klickt — Step 5)"
        : "(wartet)",
      optional: true,
    },
  ];

  return (
    <div>
      <PhaseHeader
        num={3}
        title="Validierung (Diäten-Pfad)"
        purpose="Diäten-OCR-Output von Birgit prüfen, korrigieren, freigeben"
        inputs="card.data.entries[] + data.header + card.state ∈ {ocr_complete, pending_review}"
        outputs="korrigierte data.entries[] + card.employee_id + state=validated"
        prodFiles={[
          "frontend/src/pages/ExpenseDetailPage.tsx",
          "hub/src/routes/expenses.ts",
          "hub/src/repos/expense.ts (mergeCardData für entries[] — geplant analog zu timesheet)",
        ]}
        userGates="(1) Cells bestätigen/korrigieren  (2) Mitarbeiter aus Suggestions wählen  (3) Validieren klicken"
      />
      <div style={{
        background: "#fef3c7", border: "1px solid #f59e0b", padding: "8px 12px",
        borderRadius: 4, marginBottom: 12, fontSize: 13,
      }}>
        <strong>⚙️ Diäten-Pipeline (in Aufbau)</strong> — Per-Cell-Provenance + corrections[]-
        Stack landen erst mit der Diäten-Schema-Migration in den entries-Zellen. Heute zeigt
        ExpenseDetailPage slim cells {`{value, confidence}`}.
      </div>
      <PipelineStepList steps={withUuids(c?.id ?? null, "validierung", steps)} />
      {metrics.violationsList.length > 0 && (
        <div className="violations-list" style={{ marginTop: 16 }}>
          <strong>Plausibilitäts-Verletzungen ({metrics.violationsList.length}):</strong>
          <ul style={{ fontSize: 13, marginTop: 8 }}>
            {metrics.violationsList.slice(0, 20).map((v, i) => (
              <li key={i}><strong>Eintrag #{v.entry_idx + 1}</strong> {v.field}: {v.reason}</li>
            ))}
            {metrics.violationsList.length > 20 && (
              <li className="muted">… und {metrics.violationsList.length - 20} weitere</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
